import { useState, useMemo, useRef } from 'react';
import { Rows3, Grid2x2, Columns3, Star, Eye, EyeClosed, Link2, Shuffle, Bookmark, Copy, Trash2, Grid2x2Plus, SquareSlash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { ColorMoment, LayoutMode } from '@/hooks/useColorMoments';
import { COLOR_MOMENT_KIND } from '@/hooks/useColorMoments';
import { useMomentReactions } from '@/hooks/useMomentReactions';
import { useAuthor } from '@/hooks/useAuthor';
import { EmojiPicker } from './EmojiPicker';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';

import { useQueryClient } from '@tanstack/react-query';
import { genUserName } from '@/lib/genUserName';
import { getEmojiToast } from '@/lib/emojiToasts';
import { formatColorsForCopy } from '@/lib/utils';
import { getUrlCopyToast, getColorCopyToast, getBookmarkAddToast, getBookmarkRemoveToast, getDeleteConfirmToast, getDeleteSuccessToast } from '@/lib/copyToasts';
import { ColorPaletteDisplay } from './ColorPaletteDisplay';

const LAYOUT_ICONS: Record<LayoutMode, typeof Rows3> = {
  horizontal: Rows3,
  grid: Grid2x2,
  vertical: Columns3,
  star: Star,
  checkerboard: Grid2x2Plus,
  diagonalStripes: SquareSlash,
};

/**
 * Get available layouts based on color count.
 * Grid is only available for 4 or 6 colors.
 * Star is not available for 4 colors (looks like a square/grid).
 * Checkerboard and diagonal stripes are always available.
 */
function getAvailableLayouts(colorCount: number): LayoutMode[] {
  const layouts: LayoutMode[] = ['horizontal', 'vertical'];
  // Grid only works well with 4 or 6 colors
  if (colorCount === 4 || colorCount === 6) {
    layouts.push('grid');
  }
  // Star doesn't work well with 4 colors (just makes a square)
  if (colorCount !== 4) {
    layouts.push('star');
  }
  // Checkerboard and diagonal stripes are always available
  layouts.push('checkerboard', 'diagonalStripes');
  return layouts;
}

interface ColorMomentCardProps {
  moment: ColorMoment;
  onRemix?: (colors: string[], buttonRect: DOMRect) => void;
}

export function ColorMomentCard({ moment, onRemix }: ColorMomentCardProps) {
  const [showInfo, setShowInfo] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Track the current moment ID to detect when it changes (component reuse)
  const currentMomentIdRef = useRef(moment.event.id);
  
  // Reset state if the moment changes (component was reused for different event)
  if (currentMomentIdRef.current !== moment.event.id) {
    currentMomentIdRef.current = moment.event.id;
    // These would be stale from previous moment
    if (deleteConfirm) setDeleteConfirm(false);
    if (isDeleted) setIsDeleted(false);
  }
  
  const availableLayouts = getAvailableLayouts(moment.colors.length);
  // If saved layout is grid but not available, fall back to horizontal
  const initialLayout = moment.layout && availableLayouts.includes(moment.layout) 
    ? moment.layout 
    : 'horizontal';
  const [layout, setLayout] = useState<LayoutMode>(initialLayout);
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: createEvent, isPending: isPublishing } = useNostrPublish();
  const { data: reactions = [] } = useMomentReactions(moment.event.id);
  const author = useAuthor(moment.event.pubkey);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if current user is the creator of this moment
  const isCreator = user?.pubkey === moment.event.pubkey;

  // Fetch user's cherished hues to check bookmark status
  const { data: cherishedEvent } = useQuery({
    queryKey: ['cherished-hues-event', user?.pubkey],
    queryFn: async () => {
      if (!user?.pubkey) return null;
      const events = await nostr.query([
        {
          kinds: [30003],
          authors: [user.pubkey],
          '#d': ['cherished-hues'],
          limit: 1,
        },
      ]);
      return events[0] || null;
    },
    enabled: !!user?.pubkey,
  });

  // Check if current moment is bookmarked
  const isBookmarked = useMemo(() => {
    if (!cherishedEvent) return false;
    return cherishedEvent.tags.some(
      ([name, id]) => name === 'e' && id === moment.event.id
    );
  }, [cherishedEvent, moment.event.id]);
  
  const authorName = author.data?.metadata?.name || genUserName(moment.event.pubkey);
  const authorNpub = nip19.npubEncode(moment.event.pubkey);
  const eventNevent = nip19.neventEncode({ id: moment.event.id, author: moment.event.pubkey });

  const handleReaction = (emoji: string) => {
    if (!user) return;

    createEvent({
      kind: 7,
      content: emoji,
      tags: [
        ['e', moment.event.id],
        ['p', moment.event.pubkey],
      ],
    }, {
      onSuccess: () => {
        const toastMessage = getEmojiToast(emoji, 'reaction');
        toast({ title: `${emoji} ${toastMessage}` });
        queryClient.invalidateQueries({ queryKey: ['moment-reactions', moment.event.id] });
      },
    });
  };

  // Show more reactions when info is expanded, otherwise limit to 4 to prevent overflow
  // (reactions with counts take more space, so 4 fits better than 5)
  const topReactions = showInfo ? reactions : reactions.slice(0, 4);

  const handleToggleInfo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInfo(!showInfo);
  };

  const handleToggleLayout = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = availableLayouts.indexOf(layout);
    const nextIndex = (currentIndex + 1) % availableLayouts.length;
    setLayout(availableLayouts[nextIndex]);
  };

  const handleCopyLink = () => {
    // Use production URL for sharing, fallback to current origin in dev
    const baseUrl = import.meta.env.PROD ? 'https://espy.you' : window.location.origin;
    const url = `${baseUrl}/${eventNevent}`;
    navigator.clipboard.writeText(url);
    toast({ title: getUrlCopyToast() });
  };

  const handleCopyColors = (e: React.MouseEvent) => {
    e.stopPropagation();
    const colorList = formatColorsForCopy(moment.colors);
    navigator.clipboard.writeText(colorList);
    toast({ title: getColorCopyToast() });
  };

  const handleRemix = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onRemix) {
      const rect = e.currentTarget.getBoundingClientRect();
      onRemix(moment.colors, rect);
    }
  };

  const handleBookmark = () => {
    if (!user) return;
    
    if (isBookmarked) {
      // Remove bookmark - publish updated list without this event
      const existingETags = cherishedEvent?.tags.filter(([name]) => name === 'e') || [];
      const newETags = existingETags.filter(([, id]) => id !== moment.event.id);
      
      createEvent({
        kind: 30003,
        content: '',
        tags: [
          ['d', 'cherished-hues'],
          ...newETags,
        ],
      }, {
        onSuccess: () => {
          toast({ title: getBookmarkRemoveToast() });
          queryClient.invalidateQueries({ queryKey: ['cherished-hues'] });
          queryClient.invalidateQueries({ queryKey: ['cherished-hues-event', user.pubkey] });
        },
      });
    } else {
      // Add bookmark - publish updated list with this event
      const existingETags = cherishedEvent?.tags.filter(([name]) => name === 'e') || [];
      
      createEvent({
        kind: 30003,
        content: '',
        tags: [
          ['d', 'cherished-hues'],
          ...existingETags,
          ['e', moment.event.id],
        ],
      }, {
        onSuccess: () => {
          toast({ title: getBookmarkAddToast() });
          queryClient.invalidateQueries({ queryKey: ['cherished-hues'] });
          queryClient.invalidateQueries({ queryKey: ['cherished-hues-event', user.pubkey] });
        },
      });
    }
  };

  // Store the event ID we're confirming deletion for
  const deleteTargetRef = useRef<string | null>(null);

  const handleDelete = () => {
    if (!user || !isCreator) return;

    if (!deleteConfirm) {
      // First tap - show confirmation and capture the target ID
      deleteTargetRef.current = moment.event.id;
      setDeleteConfirm(true);
      toast({ title: getDeleteConfirmToast() });
      
      // Reset confirmation state after 3 seconds
      deleteTimeoutRef.current = setTimeout(() => {
        setDeleteConfirm(false);
        deleteTargetRef.current = null;
      }, 3000);
    } else {
      // Second tap - perform deletion using the captured ID
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
      
      const targetId = deleteTargetRef.current;
      if (!targetId) return;
      
      createEvent({
        kind: 5,
        content: '',
        tags: [
          ['e', targetId],
          ['k', String(COLOR_MOMENT_KIND)],
        ],
      }, {
        onSuccess: () => {
          setIsDeleted(true);
          setDeleteConfirm(false);
          deleteTargetRef.current = null;
          toast({ title: getDeleteSuccessToast() });
          queryClient.invalidateQueries({ queryKey: ['color-moments-infinite'] });
          queryClient.invalidateQueries({ queryKey: ['following-moments-infinite'] });
          queryClient.invalidateQueries({ queryKey: ['user-color-moments'] });
          queryClient.invalidateQueries({ queryKey: ['cherished-hues'] });
        },
        onError: () => {
          setDeleteConfirm(false);
          deleteTargetRef.current = null;
        },
      });
    }
  };

  const LayoutIcon = LAYOUT_ICONS[layout] || Rows3;

  // Don't render if deleted
  if (isDeleted) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Color Display */}
      <ColorPaletteDisplay
        colors={moment.colors}
        layout={layout}
        className="relative aspect-square rounded-3xl overflow-hidden cursor-pointer group"
        onClick={() => setShowInfo(!showInfo)}
      >
        {/* Emoji overlay */}
        {moment.emoji && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-8xl drop-shadow-xl group-hover:scale-110 transition-transform duration-300">
              {moment.emoji}
            </span>
          </div>
        )}

        {/* Layout toggle button - bottom left, only visible when info is open */}
        {showInfo && (
          <div className="absolute bottom-3 left-3 flex items-center gap-2 z-10">
            <button
              onClick={handleToggleLayout}
              className="p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all"
              title="Change layout"
            >
              <LayoutIcon className="h-4 w-4" />
            </button>
            <button
              onClick={handleCopyColors}
              className="p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all"
              title="Copy colors"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Eye button - bottom right, toggles info visibility */}
        <button
          onClick={handleToggleInfo}
          className="absolute bottom-3 right-3 p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-[background-color,transform] duration-150 z-10"
          title={showInfo ? "Hide info" : "Show info"}
        >
          {showInfo ? <Eye className="h-4 w-4" size={16} /> : <EyeClosed className="h-4 w-4" size={16} />}
        </button>

        {/* Tap hint */}
        {!showInfo && (
          <div className="absolute inset-0 flex items-end justify-center pb-14 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <span className="bg-black/30 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
              tap to expand
            </span>
          </div>
        )}
      </ColorPaletteDisplay>

      {/* Expandable details section */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: showInfo ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 pt-1">
            {/* Name, creator, and actions */}
            <div className="px-2 flex items-center gap-3">
              {/* Left side - name and author */}
              <div className="flex-1 min-w-0">
                {moment.name && (
                  <h3 className="text-lg font-medium truncate">
                    {moment.name}
                  </h3>
                )}
                <Link 
                  to={`/${authorNpub}`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  by {authorName}
                </Link>
              </div>
              
              {/* Right side - action buttons */}
              <div className="flex items-center gap-1">
                {/* Delete - only shown for creators */}
                {isCreator && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      deleteConfirm
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-red-500'
                    }`}
                    title={deleteConfirm ? 'Tap again to confirm' : 'Delete this moment'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}

                {/* Remix */}
                {onRemix && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemix(e);
                    }}
                    className="p-2 rounded-full bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                    title="Remix this palette"
                  >
                    <Shuffle className="h-4 w-4" />
                  </button>
                )}
                
                {/* Bookmark */}
                {user && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookmark();
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      isBookmarked 
                        ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400' 
                        : 'bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                    title={isBookmarked ? "Remove from cherished hues" : "Add to cherished hues"}
                  >
                    <Bookmark className="h-4 w-4" fill={isBookmarked ? "currentColor" : "none"} />
                  </button>
                )}
                
                {/* Copy link */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyLink();
                  }}
                  className="p-2 rounded-full bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  title="Copy link"
                >
                  <Link2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Emoji Picker */}
            {user && (
              <EmojiPicker onSelect={handleReaction} maxHeight={180} disabled={isPublishing} />
            )}
          </div>
        </div>
      </div>

      {/* Reaction Row - always visible */}
      <div className="flex items-center gap-2 px-2">
        {/* Existing reactions - wrap when info is shown, otherwise flex in a row */}
        <div className={`flex items-center gap-2 min-w-0 ${showInfo ? 'flex-wrap flex-1' : 'overflow-hidden'}`}>
          {topReactions.map(({ emoji, count }, index) => (
            <button
              key={index}
              onClick={() => handleReaction(emoji)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary/50 hover:bg-secondary transition-colors flex-shrink-0"
            >
              <span className="text-xl">{emoji}</span>
              {count > 1 && (
                <span className="text-sm text-muted-foreground">{count}</span>
              )}
            </button>
          ))}
          {/* Show indicator for more reactions when collapsed */}
          {!showInfo && reactions.length > 4 && (
            <span className="text-sm text-muted-foreground flex-shrink-0 ml-2">
              +{reactions.length - 4}
            </span>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />
      </div>
    </div>
  );
}
